import React, { useEffect, useState, useCallback } from "react";
import { MdCheckCircle, MdOutlineRadioButtonUnchecked, MdContentCopy, MdExpandMore, MdLightbulb, MdTrendingUp, MdAttachMoney, MdSchedule } from "react-icons/md";
import toast from "react-hot-toast";
import api from "../../../services/api";

export default function Upsell({ user }) {
  const [result, setResult] = useState([]);
  const [copied, setCopied] = useState(false);
  const [isAnyLoading, setIsAnyLoading] = useState(false);
  const [showXml, setShowXml] = useState(false);
  const [dataSourceCounts, setDataSourceCounts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [xml, setXml] = useState("");
  const [recommendations, setRecommendations] = useState([]);

  const handleAddXml = useCallback(
    (xml, type, count = 0) => {
      setResult((prev) => {
        const newResult = [...prev, { xml, type }];
        setXml(newResult.map((e) => e.xml).join("\n"));
        return newResult;
      });
      setDataSourceCounts((prev) => ({ ...prev, [type]: count }));
    },
    [],
  );

  const handleLoadingChange = useCallback((isLoading) => {
    setIsAnyLoading(isLoading);
  }, []);

  function handleCopy() {
    const text = (result || []).map((e) => e.xml).join("\n");
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("XML copied to clipboard.");
    setTimeout(() => setCopied(false), 1200);
  }

  async function handleGenerateRecommendations() {
    if (!xml.trim()) return toast.error("Please wait for data sources to load first");

    console.log("XML being sent to AI:", xml);
    setIsLoading(true);
    try {
      const prompt = `You are a business development expert. Your ONLY job is to find ways to sell MORE services to EXISTING CLIENTS who already have active projects.

DO NOT SUGGEST:
❌ Career development for the person
❌ Training for the person  
❌ Job changes or role improvements
❌ Internal company improvements
❌ General business advice

ONLY SUGGEST:
✅ Ways to sell MORE to existing clients
✅ Additional features for current projects
✅ Maintenance contracts after project completion
✅ Phase 2/3 development for existing projects
✅ Premium upgrades to current solutions
✅ Additional services to current clients

EXAMPLE GOOD RECOMMENDATIONS (focus on HOW to achieve the IF condition):
- "IPEM: Upsell €300K if we deliver working MVP - Focus on user testing, performance optimization, and client demos to ensure success"
- "Manchester Project: €50K yearly contract if we meet KPIs - Prioritize data accuracy, response times, and user adoption metrics"  
- "JobFirst Platform: Scale to €4K/month if MVP gets traction - Implement analytics, user feedback loops, and marketing support"
- "E-commerce Dashboard: €25K analytics upgrade if current phase shows ROI - Track conversion rates, provide detailed reporting, highlight cost savings"

EXAMPLE BAD RECOMMENDATIONS (DO NOT DO THIS):
- "Improve leadership skills"
- "Learn new technologies"  
- "Better time management"
- "Attend conferences"

MANDATORY DATA-DRIVEN APPROACH:
1. ONLY use projects listed in the XML <user_projects> section
2. ONLY use client names that appear in the project data
3. ONLY reference budget data from <user_budgets> section
4. ONLY reference activities from <user_activities> section
5. ONLY reference notes from <user_notes> section

REQUIRED FORMAT FOR EACH RECOMMENDATION:
- Project Name: [EXACT name from XML data]
- Client Name: [EXACT client from XML data] 
- Recommendation: "[PROJECT NAME]: Upsell €[AMOUNT] if [SUCCESS CONDITION] - [what to sell]"
- Success Condition: What must be achieved to unlock the upsell
- How to Succeed: Specific strategic advice on HOW to achieve the success condition
- Activation Trigger: When/how to approach the client for the upsell

ABSOLUTELY FORBIDDEN:
- Generic client names like "Client A", "Unknown", "Key Clients"
- Generic project names not in the data
- Vague terms like "capacity gaps", "resource allocation"
- Any recommendation without specific project + client from XML data

STRATEGIC FOCUS:
- The "HOW TO SUCCEED" section is the most important part
- Provide actionable advice on achieving the success condition
- Think like a project consultant: What specific actions, metrics, or focus areas will ensure success?
- Consider technical, business, and relationship factors that drive project success

FLEXIBLE APPROACH:
- If projects have client names: Generate specific client-focused recommendations
- If projects lack client names but have descriptions: Generate service expansion recommendations 
- If very limited project data: Generate general upselling strategies based on available data
- Always prioritize specific over general recommendations

Generate up to 10 upselling opportunities with strong focus on HOW TO SUCCEED strategies.`;

      const { ok, data } = await api.post("/upsell_recommendations/generate", {
        prompt: prompt,
        xml: xml,
      });

      if (!ok) {
        toast.error("Failed to generate recommendations");
        return;
      }

      setRecommendations(data || []);
      if (data && data.length > 0) {
        toast.success(`Generated ${data.length} upsell recommendations`);
      }
    } catch (error) {
      console.error("Error generating recommendations:", error);
      toast.error("Failed to generate recommendations");
    } finally {
      setIsLoading(false);
    }
  }

  const completedSteps = result.length;
  const totalSteps = 7;

  return (
    <div className="max-w-full mx-auto">
      <div className="bg-white rounded-lg shadow flex flex-row">
        <div className="w-1/3 p-4 border-r border-gray-200 flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-3">
              <h3 className="text-base font-semibold">Data Sources</h3>
              <div className="flex items-center gap-2">
                <div className="w-12 bg-blue-200 rounded-full h-1">
                  <div className="bg-blue-600 h-1 rounded-full transition-all duration-300" style={{ width: `${(completedSteps / totalSteps) * 100}%` }}></div>
                </div>
                <span className="text-xs text-blue-600">
                  {completedSteps}/{totalSteps}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isAnyLoading && (
                <div className="flex items-center gap-1 text-blue-600 text-xs">
                  <MdOutlineRadioButtonUnchecked className="animate-spin text-xs" />
                  Loading...
                </div>
              )}
            </div>
          </div>

          {/* Data Source Cards */}
          <div className="grid grid-cols-1 gap-2 mb-3">
            <DataSourceCard
              title="User Profile"
              component={<UserProfileXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.user_profile || 0}
              description="Basic profile data"
            />
            <DataSourceCard
              title="Active Projects"
              component={<UserProjectsXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.user_projects || 0}
              description="Leading projects"
            />
            <DataSourceCard
              title="Project Budgets"
              component={<UserBudgetsXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.user_budgets || 0}
              description="Budget utilization"
            />
            <DataSourceCard
              title="Recent Activities"
              component={<UserActivitiesXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.user_activities || 0}
              description="Last 3 months"
            />
            <DataSourceCard
              title="Notes & Feedback"
              component={<UserNotesXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.user_notes || 0}
              description="Client feedback"
            />
            <DataSourceCard
              title="Budget Issues"
              component={<BudgetIssuesXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.budget_issues || 0}
              description="Current issues"
            />
            <DataSourceCard
              title="Project Roadmaps"
              component={<ProjectRoadmapXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.project_roadmaps || 0}
              description="Epics & features"
            />
          </div>

          {/* Collapsible XML Section */}
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <button onClick={() => setShowXml(!showXml)} className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800">
                <MdExpandMore className={`transition-transform text-xs ${showXml ? "rotate-180" : ""}`} />
                {showXml ? "Hide" : "Show"} XML Data
              </button>
              <button
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-gray-200 hover:bg-gray-300 transition ${copied ? "text-green-600" : "text-gray-700"}`}
                onClick={handleCopy}
                aria-label="Copy XML"
                type="button">
                <MdContentCopy className="inline text-xs" />
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            {showXml && <pre className="text-xs bg-gray-50 p-2 rounded max-h-48 overflow-auto border">{(result || []).map((e) => e.xml).join("\n")}</pre>}
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerateRecommendations}
            disabled={isLoading || !xml.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {isLoading && <MdOutlineRadioButtonUnchecked className="animate-spin" />}
            {isLoading ? "Generating..." : "Generate Recommendations"}
          </button>
          {!xml.trim() && <p className="text-xs text-gray-500 mt-2">Wait for data sources to load before generating recommendations</p>}
        </div>

        <div className="w-2/3 p-6">
          <div className="h-full flex flex-col">
            {recommendations.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MdLightbulb className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No recommendations yet</h3>
                  <p className="text-gray-600">Load data sources and generate upsell recommendations</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-semibold">Upsell Recommendations ({recommendations.length})</h4>
                  <button
                    onClick={handleGenerateRecommendations}
                    disabled={isLoading}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50">
                    Regenerate
                  </button>
                </div>
                {recommendations.map((rec, index) => (
                  <RecommendationCard key={index} recommendation={rec} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DataSourceCard({ title, component, count, description }) {
  return (
    <div className="p-2 border border-gray-200 rounded hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-xs font-medium text-gray-700">{title}</span>
        {count > 0 && <span className="text-xs text-blue-600 bg-blue-50 px-1 rounded">{count}</span>}
      </div>
      <div className="text-xs text-gray-500 mb-1">{description}</div>
      {component}
    </div>
  );
}

// User Profile XML Component
function UserProfileXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    generateUserProfile();
  }, [user]);

  async function generateUserProfile() {
    setIsLoading(true);
    onLoadingChange(true);
    try {
      const xml = `<user_profile>
  <id>${user._id}</id>
  <name>${user.name || ""}</name>
  <role>${user.job_title || ""}</role>
  <contract>${user.contract || ""}</contract>
  <office>${user.office || ""}</office>
  <manager>${user.manager_name || ""}</manager>
  <tjm>${user.tjm || 0}</tjm>
  <tjms>${user.tjms || 0}</tjms>
  <main_objective>${user.main_objective || ""}</main_objective>
</user_profile>`;

      onAdd(xml, "user_profile", 1);
    } catch (error) {
      console.error("Error generating user profile:", error);
    } finally {
      setIsLoading(false);
      onLoadingChange(false);
    }
  }

  return (
    <div className="w-full text-left text-xs flex items-center gap-2">
      {isLoading ? (
        <>
          <MdOutlineRadioButtonUnchecked className="inline text-blue-400 animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          <MdCheckCircle className="inline text-green-500" />
          <span>Profile loaded</span>
        </>
      )}
    </div>
  );
}

// User Projects XML Component
function UserProjectsXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchUserProjects();
  }, [user]);

  async function fetchUserProjects() {
    setIsLoading(true);
    onLoadingChange(true);
    try {
      const { data: projectData, ok: projectOk } = await api.post("/project/search", {
        status: "active",
        $or: [{ lead_id: user._id }, { lead_dev_id: user._id }, { lead_product_id: user._id }],
      });

      const projects = projectOk ? projectData.projects || [] : [];
      let xml = "<user_projects>\n";

      for (const project of projects) {
        xml += `  <project>
    <id>${project._id}</id>
    <name>${project.name || ""}</name>
    <type>${project.type || ""}</type>
    <status>${project.status || ""}</status>
    <description>${project.description || ""}</description>
    <client>${project.client || ""}</client>
    <lead_role>${project.lead_id === user._id ? "lead" : project.lead_dev_id === user._id ? "lead_dev" : "lead_product"}</lead_role>
    <start_date>${project.start_date || ""}</start_date>
    <end_date>${project.end_date || ""}</end_date>
  </project>\n`;
      }

      xml += "</user_projects>";

      onAdd(xml, "user_projects", projects.length);
    } catch (error) {
      console.error("Error fetching projects:", error);
      onAdd("<user_projects></user_projects>", "user_projects", 0);
    } finally {
      setIsLoading(false);
      onLoadingChange(false);
    }
  }

  return (
    <div className="w-full text-left text-xs flex items-center gap-2">
      {isLoading ? (
        <>
          <MdOutlineRadioButtonUnchecked className="inline text-blue-400 animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          <MdCheckCircle className="inline text-green-500" />
          <span>Projects loaded</span>
        </>
      )}
    </div>
  );
}

// User Budgets XML Component
function UserBudgetsXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchUserBudgets();
  }, [user]);

  async function fetchUserBudgets() {
    setIsLoading(true);
    onLoadingChange(true);
    try {
      const { data: projectData, ok: projectOk } = await api.post("/project/search", {
        status: "active",
        $or: [{ lead_id: user._id }, { lead_dev_id: user._id }, { lead_product_id: user._id }],
      });

      const projects = projectOk ? projectData.projects || [] : [];
      const projectIds = projects.map((p) => p._id);

      if (projectIds.length === 0) {
        onAdd("<user_budgets></user_budgets>", "user_budgets", 0);
        return;
      }

      const { data: budgetData, ok: budgetOk } = await api.post("/budget/search", {
        status: "active",
        projectId: { $in: projectIds },
      });

      const budgets = budgetOk ? budgetData || [] : [];
      let xml = "<user_budgets>\n";

      for (const budget of budgets) {
        xml += `  <budget>
    <id>${budget._id}</id>
    <name>${budget.name || ""}</name>
    <project_id>${budget.projectId || ""}</project_id>
    <project_name>${budget.projectName || ""}</project_name>
    <amount>${budget.amount || 0}</amount>
    <amount_used>${budget.amountUsed || 0}</amount_used>
    <start_date>${budget.startAt || ""}</start_date>
    <end_date>${budget.endAt || ""}</end_date>
    <type>${budget.type || ""}</type>
    <confidence>${budget.confidence || 0}</confidence>
    <utilization_rate>${budget.amount > 0 ? (((budget.amountUsed || 0) / budget.amount) * 100).toFixed(1) : 0}%</utilization_rate>
  </budget>\n`;
      }

      xml += "</user_budgets>";

      onAdd(xml, "user_budgets", budgets.length);
    } catch (error) {
      console.error("Error fetching budgets:", error);
      onAdd("<user_budgets></user_budgets>", "user_budgets", 0);
    } finally {
      setIsLoading(false);
      onLoadingChange(false);
    }
  }

  return (
    <div className="w-full text-left text-xs flex items-center gap-2">
      {isLoading ? (
        <>
          <MdOutlineRadioButtonUnchecked className="inline text-blue-400 animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          <MdCheckCircle className="inline text-green-500" />
          <span>Budgets loaded</span>
        </>
      )}
    </div>
  );
}

// User Activities XML Component
function UserActivitiesXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchUserActivities();
  }, [user]);

  async function fetchUserActivities() {
    setIsLoading(true);
    onLoadingChange(true);
    try {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

      const { data: activityData, ok: activityOk } = await api.post("/activity/search", {
        userId: user._id,
        date: { $gte: threeMonthsAgo.toISOString() },
        per_page: 20,
      });

      const activities = activityOk ? activityData || [] : [];
      let xml = "<user_activities>\n";

      for (const activity of activities) {
        xml += `  <activity>
    <id>${activity._id}</id>
    <date>${activity.date || ""}</date>
    <description>${activity.description || ""}</description>
    <duration>${activity.duration || 0}</duration>
    <project_id>${activity.projectId || ""}</project_id>
    <project_name>${activity.projectName || ""}</project_name>
    <type>${activity.type || ""}</type>
  </activity>\n`;
      }

      xml += "</user_activities>";

      onAdd(xml, "user_activities", activities.length);
    } catch (error) {
      console.error("Error fetching activities:", error);
      onAdd("<user_activities></user_activities>", "user_activities", 0);
    } finally {
      setIsLoading(false);
      onLoadingChange(false);
    }
  }

  return (
    <div className="w-full text-left text-xs flex items-center gap-2">
      {isLoading ? (
        <>
          <MdOutlineRadioButtonUnchecked className="inline text-blue-400 animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          <MdCheckCircle className="inline text-green-500" />
          <span>Activities loaded</span>
        </>
      )}
    </div>
  );
}

// User Notes XML Component
function UserNotesXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchUserNotes();
  }, [user]);

  async function fetchUserNotes() {
    setIsLoading(true);
    onLoadingChange(true);
    try {
      const { data: notesData, ok: notesOk } = await api.post("/meeting_note/search", {
        user_id: user._id,
      });

      const notes = notesOk ? notesData || [] : [];
      let xml = "<user_notes>\n";

      for (const note of notes) {
        xml += `  <note>
    <id>${note._id}</id>
    <date>${note.created_at || ""}</date>
    <content>${(note.text || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</content>
    <author>${note.author_name || ""}</author>
  </note>\n`;
      }
      xml += "</user_notes>";

      onAdd(xml, "user_notes", notes.length);
    } catch (error) {
      console.error("Error fetching notes:", error);
      onAdd("<user_notes></user_notes>", "user_notes", 0);
    } finally {
      setIsLoading(false);
      onLoadingChange(false);
    }
  }

  return (
    <div className="w-full text-left text-xs flex items-center gap-2">
      {isLoading ? (
        <>
          <MdOutlineRadioButtonUnchecked className="inline text-blue-400 animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          <MdCheckCircle className="inline text-green-500" />
          <span>Notes loaded</span>
        </>
      )}
    </div>
  );
}

// Budget Issues XML Component
function BudgetIssuesXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchBudgetIssues();
  }, [user]);

  async function fetchBudgetIssues() {
    setIsLoading(true);
    onLoadingChange(true);
    try {
      const { data: budgetIssues, ok: budgetOk } = await api.post("/budget_check/analyze", {
        user_id: user._id,
      });

      if (budgetOk && budgetIssues && budgetIssues.length > 0) {
        let xml = "<budget_issues>\n";
        for (const issue of budgetIssues.slice(0, 5)) {
          xml += `  <issue>
    <project_name>${issue.projectName || ""}</project_name>
    <problem>${issue.problem || ""}</problem>
    <action>${issue.action || ""}</action>
    <days_remaining>${issue.daysRemaining || ""}</days_remaining>
  </issue>\n`;
        }
        xml += "</budget_issues>";
        onAdd(xml, "budget_issues", budgetIssues.length);
      } else {
        onAdd("<budget_issues></budget_issues>", "budget_issues", 0);
      }
    } catch (error) {
      console.error("Error fetching budget issues:", error);
      onAdd("<budget_issues></budget_issues>", "budget_issues", 0);
    } finally {
      setIsLoading(false);
      onLoadingChange(false);
    }
  }

  return (
    <div className="w-full text-left text-xs flex items-center gap-2">
      {isLoading ? (
        <>
          <MdOutlineRadioButtonUnchecked className="inline text-blue-400 animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          <MdCheckCircle className="inline text-green-500" />
          <span>Issues loaded</span>
        </>
      )}
    </div>
  );
}

// Project Roadmap XML Component
function ProjectRoadmapXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchProjectRoadmaps();
  }, [user]);

  async function fetchProjectRoadmaps() {
    setIsLoading(true);
    onLoadingChange(true);
    try {
      // First get user's active projects
      const { data: projectData, ok: projectOk } = await api.post("/project/search", { 
        status: "active",
        $or: [
          { lead_id: user._id },
          { lead_dev_id: user._id },
          { lead_product_id: user._id }
        ]
      });

      const projects = projectOk ? (projectData.projects || []) : [];
      const projectIds = projects.map(p => p._id);

      if (projectIds.length === 0) {
        onAdd("<project_roadmaps></project_roadmaps>", "project_roadmaps", 0);
        return;
      }

      // Get epics for these projects
      const { data: epicsData, ok: epicsOk } = await api.post("/epic/search", {
        project_id: { $in: projectIds },
        per_page: 50
      });

      const epics = epicsOk ? (epicsData || []) : [];
      let xml = "<project_roadmaps>\n";
      
      for (const epic of epics) {
        xml += `  <epic>
    <id>${epic._id}</id>
    <title>${(epic.title || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</title>
    <project_id>${epic.project_id || ""}</project_id>
    <project_name>${epic.project_name || ""}</project_name>
    <status>${epic.status || ""}</status>
    <priority>${epic.priority || ""}</priority>
    <description>${(epic.description || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</description>
    <budget_name>${epic.budget_name || ""}</budget_name>
    <days_required>${epic.days_required || 0}</days_required>
    <tjm>${epic.tjm || 0}</tjm>
    <module>${epic.module || ""}</module>
    <created_at>${epic.created_at || ""}</created_at>
  </epic>\n`;
      }
      
      xml += "</project_roadmaps>";
      
      onAdd(xml, "project_roadmaps", epics.length);
    } catch (error) {
      console.error("Error fetching project roadmaps:", error);
      onAdd("<project_roadmaps></project_roadmaps>", "project_roadmaps", 0);
    } finally {
      setIsLoading(false);
      onLoadingChange(false);
    }
  }

  return (
    <div className="w-full text-left text-xs flex items-center gap-2">
      {isLoading ? (
        <>
          <MdOutlineRadioButtonUnchecked className="inline text-blue-400 animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          <MdCheckCircle className="inline text-green-500" />
          <span>Roadmaps loaded</span>
        </>
      )}
    </div>
  );
}

function RecommendationCard({ recommendation }) {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "HIGH":
        return "text-red-600 bg-red-50";
      case "MEDIUM":
        return "text-yellow-600 bg-yellow-50";
      case "LOW":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "Scope Expansion":
        return "text-blue-600 bg-blue-50";
      case "Maintenance Contract":
        return "text-green-600 bg-green-50";
      case "Feature Enhancement":
        return "text-purple-600 bg-purple-50";
      case "Platform Upgrade":
        return "text-orange-600 bg-orange-50";
      case "Additional Services":
        return "text-pink-600 bg-pink-50";
      case "Extended Timeline":
        return "text-indigo-600 bg-indigo-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  return (
    <div className="bg-white rounded border p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <MdTrendingUp className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{recommendation.project_name}</h4>
            {recommendation.client_name && <p className="text-xs text-gray-600">Client: {recommendation.client_name}</p>}
            <p className="text-xs text-gray-500">{recommendation.opportunity_type}</p>
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(recommendation.priority)}`}>{recommendation.priority}</span>
          {recommendation.category && <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(recommendation.category)}`}>{recommendation.category}</span>}
        </div>
      </div>

      <p className="text-gray-700 text-sm mb-3">{recommendation.recommendation}</p>

      {recommendation.success_condition && (
        <div className="bg-green-50 p-3 rounded mb-2">
          <p className="text-xs font-medium text-green-800 mb-1">Success Condition:</p>
          <p className="text-xs text-green-700">{recommendation.success_condition}</p>
        </div>
      )}

      {recommendation.how_to_succeed && (
        <div className="bg-yellow-50 p-3 rounded mb-2 border-l-4 border-yellow-400">
          <p className="text-xs font-medium text-yellow-800 mb-1">💡 How to Succeed:</p>
          <p className="text-xs text-yellow-700">{recommendation.how_to_succeed}</p>
        </div>
      )}

      {recommendation.activation_trigger && (
        <div className="bg-orange-50 p-3 rounded mb-2">
          <p className="text-xs font-medium text-orange-800 mb-1">Activation Trigger:</p>
          <p className="text-xs text-orange-700">{recommendation.activation_trigger}</p>
        </div>
      )}

      {recommendation.business_justification && (
        <div className="bg-blue-50 p-3 rounded mb-3">
          <p className="text-xs font-medium text-blue-800 mb-1">Business Justification:</p>
          <p className="text-xs text-blue-700">{recommendation.business_justification}</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
        {recommendation.potential_revenue && (
          <div className="flex items-center gap-1">
            <MdAttachMoney className="w-3 h-3 text-green-600" />
            <span className="text-green-600 font-medium">€{recommendation.potential_revenue?.toLocaleString()}</span>
          </div>
        )}
        {recommendation.timeline && (
          <div className="flex items-center gap-1">
            <MdSchedule className="w-3 h-3 text-purple-600" />
            <span className="text-purple-600 font-medium">{recommendation.timeline}</span>
          </div>
        )}
        {recommendation.confidence && (
          <div className="text-gray-600">
            <span className="font-medium">{recommendation.confidence}%</span>
          </div>
        )}
      </div>

      {recommendation.next_steps && recommendation.next_steps.length > 0 && (
        <div>
          <p className="font-medium text-gray-900 text-xs mb-1">Next Steps:</p>
          <ul className="list-disc list-inside space-y-0.5 text-xs text-gray-600">
            {recommendation.next_steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
